import { AutocompleteRenderGroupParams, styled } from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { OptionGroupHeader } from './OptionGroupHeader'

const isCollapsedGroup = (
  collapsedGroups: Record<string, boolean>,
  group: string,
  expandedGroups: string[]
): boolean => collapsedGroups[group] ?? !expandedGroups.includes(group)

type ExpandableOptionGroupsProps<Entity> = {
  options: Entity[]
  // 保持している値はグループの判定に必要な項目を欠くことがあるため、値そのものではなく
  // 候補側から選択済みのものを見つける。
  isSelected: (option: Entity) => boolean
  groupBy?: (option: Entity) => string
  // ここで指定したグループだけを開いた状態で表示する。
  defaultExpandedGroups?: string[]
  renderGroupLabel?: (group: string) => ReactNode
  // 絞り込み中は、折りたたまれたグループ内の一致候補が隠れて「検索したのに出てこない」状態に
  // なるため、全グループを開いて扱う。
  isSearching: boolean
}

export const useExpandableOptionGroups = <Entity,>({
  options,
  isSelected,
  groupBy,
  defaultExpandedGroups,
  renderGroupLabel,
  isSearching,
}: ExpandableOptionGroupsProps<Entity>) => {
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({})
  const selectedGroups = useMemo(
    () => (groupBy ? options.filter(isSelected).map(groupBy) : []),
    [groupBy, isSelected, options]
  )
  // defaultExpandedGroups に該当する候補が無いと全グループが閉じて中身が見えなくなるため、
  // その場合は先頭のグループを開く。
  const expandedGroups = useMemo(() => {
    if (!groupBy) return []
    const groups = options.map(groupBy)
    const expanded = [
      ...(defaultExpandedGroups ?? []).filter(group => groups.includes(group)),
      ...selectedGroups,
    ]
    return expanded.length > 0 ? expanded : groups.slice(0, 1)
  }, [defaultExpandedGroups, groupBy, options, selectedGroups])
  // 選択によって開いたグループを選択の有無だけで判定すると、選択を外した瞬間に閉じて
  // 同じグループの別の候補に付け替えられない。開いたことを記録し、以降は見出し操作で開閉する。
  useEffect(() => {
    if (selectedGroups.length === 0) return
    setCollapsedGroups(prev => {
      const unrecorded = selectedGroups.filter(group => !(group in prev))
      if (unrecorded.length === 0) return prev
      return {
        ...prev,
        ...Object.fromEntries(unrecorded.map(group => [group, false])),
      }
    })
  }, [selectedGroups])
  const toggleGroup = useCallback(
    (group: string) => {
      setCollapsedGroups(prev => ({
        ...prev,
        [group]: !isCollapsedGroup(prev, group, expandedGroups),
      }))
    },
    [expandedGroups]
  )
  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      // 名前の無い見出しは開閉の手掛かりにならないため、見出しごと出さない。
      if (!params.group) {
        return (
          <GroupRoot key={params.key}>
            <GroupItems>{params.children}</GroupItems>
          </GroupRoot>
        )
      }
      const isCollapsed =
        !isSearching &&
        isCollapsedGroup(collapsedGroups, params.group, expandedGroups)
      return (
        <GroupRoot key={params.key}>
          <OptionGroupHeader
            // mousedown のデフォルト挙動を止めて input のフォーカスを保持し、
            // ヘッダクリックでポップアップが閉じないようにする。
            onMouseDown={e => e.preventDefault()}
            // 絞り込み中は全グループを開いて表示しているため、操作を受け付けると
            // 見た目が変わらないまま内部状態だけが反転する。
            onClick={isSearching ? undefined : () => toggleGroup(params.group)}
            collapsible={!isSearching}
          >
            {renderGroupLabel ? renderGroupLabel(params.group) : params.group}
            {!isSearching &&
              (isCollapsed ? (
                <ExpandMore sx={{ fontSize: 16 }} />
              ) : (
                <ExpandLess sx={{ fontSize: 16 }} />
              ))}
          </OptionGroupHeader>
          {!isCollapsed && <GroupItems>{params.children}</GroupItems>}
        </GroupRoot>
      )
    },
    [
      collapsedGroups,
      expandedGroups,
      isSearching,
      renderGroupLabel,
      toggleGroup,
    ]
  )
  return { renderGroup: groupBy ? renderGroup : undefined }
}

const GroupRoot = styled('li')({
  listStyle: 'none',
})
const GroupItems = styled('ul')({
  listStyle: 'none',
  padding: 0,
})
